import { ApolloError } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { getApiErrorHandlingOptionsFromApolloContext } from "../apiRequestOptions";
import { notifyApiRequestError } from "../notifications/notifyUserFacingError";
import { isConnectionClosedMessage } from "./connectionFailure";
import { isSessionRejection } from "./sessionRejection";

/**
 * How long after the first reported session rejection further ones are not
 * reported. The window doubles with every report, up to
 * {@link SESSION_REJECTION_MAX_WINDOW_MS}: a session that really is invalid
 * must not reconnect in a loop.
 */
export const SESSION_REJECTION_WINDOW_MS = 10_000;
export const SESSION_REJECTION_MAX_WINDOW_MS = 5 * 60_000;

export type ErrorLinkCallbacks = Partial<{
  graphQl: () => void;
  network: () => void;
  /**
   * The server rejected the session of an operation. Called at most once
   * per window, which starts at {@link SESSION_REJECTION_WINDOW_MS} and
   * doubles with every call.
   */
  onSessionRejection: () => void;
}>;

export const makeErrorLink = (callbacks?: ErrorLinkCallbacks) => {
  let lastSessionRejectionAt: number | undefined;
  // Never reset: a renewed session produces no rejections, a reload starts
  // again.
  let sessionRejectionReports = 0;
  const reportSessionRejection = () => {
    const now = Date.now();
    const windowMs = Math.min(
      SESSION_REJECTION_WINDOW_MS * 2 ** (sessionRejectionReports - 1),
      SESSION_REJECTION_MAX_WINDOW_MS,
    );
    if (
      lastSessionRejectionAt !== undefined &&
      now - lastSessionRejectionAt < windowMs
    ) {
      return;
    }
    lastSessionRejectionAt = now;
    sessionRejectionReports++;
    callbacks?.onSessionRejection?.();
  };

  return new ErrorLink(({ graphQLErrors, networkError, operation }) => {
    const apolloError = new ApolloError({ graphQLErrors, networkError });
    const apiErrorHandling = getApiErrorHandlingOptionsFromApolloContext(
      operation.getContext(),
    );
    // Connection-loss noise is handled by the WS connection notifier in initApolloClient2.
    // Avoid spamming the user with near-duplicate messages like:
    // - "Socket closed"
    // - "WebSocket connection failed"
    const msg = apolloError.message ?? "";
    if (isConnectionClosedMessage(msg)) {
      return;
    }

    console.error(apolloError);

    // Avoid spamming on auth transitions (login redirects will handle this).
    // The first error describes the failure, as for the text that is shown;
    // a failure without server errors is judged by its message.
    const primaryError = graphQLErrors?.[0];
    if (
      primaryError
        ? isSessionRejection(primaryError)
        : apolloError.message.includes("Unauthorized")
    ) {
      reportSessionRejection();
      return;
    }

    notifyApiRequestError(apolloError, {
      ...apiErrorHandling,
      operation: operation.operationName,
    });
  });
};
